/**
 * POI (Points of Interest) Service
 * 
 * Uses Overpass API (OpenStreetMap) to search for nearby places
 * Free alternative to Google Places API
 */

export interface POI {
  id: string
  name: string
  type: string
  category: string
  lat: number
  lng: number
  distance?: number // meters from reference point
  address?: string
  tags: Record<string, string> // OSM tags
  
  // Optional details
  phone?: string
  website?: string
  openingHours?: string
  rating?: number
}

export type POICategory = 
  | 'fuel'
  | 'food'
  | 'accommodation'
  | 'attraction'
  | 'parking'
  | 'rest_area'
  | 'shopping'
  | 'settlement'

interface SearchOptions {
  lat: number
  lng: number
  radius?: number // meters, default 5000 (5km)
  category?: POICategory
  tags?: string[]
  limit?: number
}

type CachedPOIEntry = {
  expiresAt: number
  pois: POI[]
}

const POI_CACHE_TTL_MS = 10 * 60 * 1000
const POI_CACHE_MAX_ENTRIES = 60
const OVERPASS_RATE_LIMIT_COOLDOWN_MS = 5 * 60 * 1000

const FEATURED_ROUTE_TAGS = [
  'highway=rest_area',
  'highway=services',
  'highway=motorway_junction',
  'tourism=attraction',
  'tourism=museum',
  'tourism=viewpoint',
  'tourism=castle',
  'tourism=zoo',
  'tourism=theme_park',
  'tourism=gallery',
  'tourism=aquarium',
  'historic=monument',
  'historic=castle',
  'leisure=park'
]

function getSettlementLabelTags(zoom: number): string[] {
  if (zoom >= 11) {
    return [
      'place=city',
      'place=town',
      'place=village',
      'place=hamlet',
      'place=suburb',
      'place=municipality'
    ]
  }

  if (zoom >= 8) {
    return [
      'place=city',
      'place=town',
      'place=village'
    ]
  }

  if (zoom >= 6) {
    return [
      'place=city',
      'place=town'
    ]
  }

  if (zoom >= 5) {
    return [
      'place=city'
    ]
  }

  return []
}

const nearbyPOICache = new Map<string, CachedPOIEntry>()
const nearbyPOIRequests = new Map<string, Promise<POI[]>>()
const routePOICache = new Map<string, CachedPOIEntry>()
const routePOIRequests = new Map<string, Promise<POI[]>>()
let overpassCooldownUntil = 0

function clonePOI(poi: POI): POI {
  return {
    ...poi,
    tags: { ...poi.tags }
  }
}

function clonePOIs(pois: POI[]): POI[] {
  return pois.map(clonePOI)
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }

  return typeof error === 'string' ? error : ''
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const maybeError = error as { name?: string; message?: string }
  return maybeError.name === 'AbortError' || getErrorMessage(error).toLowerCase().includes('aborted')
}

function isOverpassRateLimitError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase()
  return message.includes('too many requests') || message.includes('429') || message.includes('rate limit')
}

function isOverpassServiceError(error: unknown): boolean {
  const message = getErrorMessage(error)
  return message === 'SERVICE_UNAVAILABLE' || message.includes('Gateway Timeout')
}

function markOverpassCooldown() {
  overpassCooldownUntil = Math.max(overpassCooldownUntil, Date.now() + OVERPASS_RATE_LIMIT_COOLDOWN_MS)
}

function isOverpassCoolingDown(): boolean {
  return Date.now() < overpassCooldownUntil
}

function normalizeCacheRadius(radius: number): number {
  return Math.max(1000, Math.round(radius / 500) * 500)
}

function normalizeCacheCoordinate(value: number): string {
  return value.toFixed(3)
}

function buildNearbyCacheKey(options: SearchOptions): string {
  const { lat, lng, radius = 5000, category, tags, limit = 20 } = options

  return [
    'nearby',
    category || 'all',
    tags?.length ? tags.slice().sort().join(',') : 'default',
    limit,
    normalizeCacheRadius(radius),
    normalizeCacheCoordinate(lat),
    normalizeCacheCoordinate(lng)
  ].join(':')
}

function buildRouteCacheKey(
  waypoints: { lat: number; lng: number }[],
  category?: POICategory,
  maxDistanceFromRoute = 5000,
  tags?: string[]
): string {
  const samplePoints = buildRouteSamplePoints(waypoints)
  const signature = samplePoints
    .map(point => `${normalizeCacheCoordinate(point.lat)},${normalizeCacheCoordinate(point.lng)}`)
    .join('|')

  return [
    'route',
    tags?.length ? tags.slice().sort().join(',') : (category || 'all'),
    normalizeCacheRadius(maxDistanceFromRoute),
    signature
  ].join(':')
}

function getFreshCacheEntry(cache: Map<string, CachedPOIEntry>, key: string): CachedPOIEntry | null {
  const entry = cache.get(key)
  if (!entry) return null

  if (entry.expiresAt <= Date.now()) {
    cache.delete(key)
    return null
  }

  return entry
}

function pruneCache(cache: Map<string, CachedPOIEntry>) {
  const now = Date.now()

  for (const [key, entry] of cache.entries()) {
    if (entry.expiresAt <= now) {
      cache.delete(key)
    }
  }

  while (cache.size > POI_CACHE_MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value
    if (!oldestKey) break
    cache.delete(oldestKey)
  }
}

async function getCachedPOIResults(
  cache: Map<string, CachedPOIEntry>,
  inFlightRequests: Map<string, Promise<POI[]>>,
  key: string,
  loader: () => Promise<POI[]>
): Promise<POI[]> {
  const cachedEntry = getFreshCacheEntry(cache, key)
  if (cachedEntry) {
    return cachedEntry.pois
  }

  const existingRequest = inFlightRequests.get(key)
  if (existingRequest) {
    return existingRequest
  }

  const request = (async () => {
    try {
      const pois = await loader()
      const cachedPois = clonePOIs(pois)

      cache.set(key, {
        expiresAt: Date.now() + (cachedPois.length === 0 ? Math.min(POI_CACHE_TTL_MS, 2 * 60 * 1000) : POI_CACHE_TTL_MS),
        pois: cachedPois
      })
      pruneCache(cache)

      return cachedPois
    } catch (error) {
      if (isAbortError(error) || isOverpassRateLimitError(error) || isOverpassServiceError(error)) {
        return []
      }

      console.warn('POI request failed:', error)
      return []
    } finally {
      inFlightRequests.delete(key)
    }
  })()

  inFlightRequests.set(key, request)
  return request
}

/**
 * Search for POIs near a location using Overpass API
 * Includes retry logic for timeout errors
 */
export async function searchNearbyPOIs(options: SearchOptions): Promise<POI[]> {
  const { lat, lng, radius = 5000, category, limit = 20 } = options

  if (isOverpassCoolingDown()) {
    return []
  }

  const cacheKey = buildNearbyCacheKey(options)

  return getCachedPOIResults(nearbyPOICache, nearbyPOIRequests, cacheKey, async () => {
    // Build Overpass query based on category
    const query = buildOverpassQuery(lat, lng, radius, category, options.tags)

    const maxRetries = 2

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const controller = new AbortController()
      let timeoutReached = false
      const timeoutId = window.setTimeout(() => {
        timeoutReached = true
        controller.abort()
      }, 15000)

      try {
        const response = await fetch('https://overpass-api.de/api/interpreter', {
          method: 'POST',
          body: query,
          signal: controller.signal
        })

        if (!response.ok) {
          if (response.status === 429) {
            markOverpassCooldown()
            return []
          }
          if (response.status === 504 || response.status === 503) {
            if (attempt < maxRetries) {
              console.log(`POI search attempt ${attempt + 1} failed, retrying...`)
              await new Promise<void>(resolve => {
                window.setTimeout(() => resolve(), (attempt + 1) * 1500)
              })
              continue
            }
            return []
          }
          return []
        }

        const data = await response.json()
        const pois = parseOverpassResponse(data, lat, lng)
        
        // Sort by distance and limit
        return pois
          .sort((a, b) => (a.distance || 0) - (b.distance || 0))
          .slice(0, limit)
      } catch (error: any) {
        if (timeoutReached || isAbortError(error)) {
          return []
        }

        if (getErrorMessage(error) === 'OVERPASS_RATE_LIMIT' || isOverpassRateLimitError(error)) {
          markOverpassCooldown()
          return []
        }
        
        // If it's a service issue, retry a little; otherwise fail softly.
        if (isOverpassServiceError(error) && attempt < maxRetries) {
          console.log(`POI search attempt ${attempt + 1} failed, retrying...`)
          // Wait before retry (exponential backoff)
          await new Promise<void>(resolve => {
            window.setTimeout(() => resolve(), (attempt + 1) * 1500)
          })
          continue
        }
        
        return []
      } finally {
        window.clearTimeout(timeoutId)
      }
    }

    return []
  })
}

export async function searchSettlementLabels(options: SearchOptions & { zoom: number }): Promise<POI[]> {
  const { zoom, limit = 14, ...searchOptions } = options
  const tags = getSettlementLabelTags(zoom)

  if (tags.length === 0) {
    return []
  }

  const pois = await searchNearbyPOIs({
    ...searchOptions,
    tags,
    limit
  })

  return pois.filter(poi => poi.name && poi.name !== 'Unnamed')
}

/**
 * Search along a route
 */
export async function searchAlongRoute(
  waypoints: { lat: number; lng: number }[],
  category?: POICategory,
  maxDistanceFromRoute = 5000
): Promise<POI[]> {
  const cacheKey = buildRouteCacheKey(waypoints, category, maxDistanceFromRoute)

  return getCachedPOIResults(routePOICache, routePOIRequests, cacheKey, async () => {
    const samplePoints = buildRouteSamplePoints(waypoints)

    // Search around sampled points along the route
    const allPOIs: POI[] = []
    const seenIds = new Set<string>()

    for (const point of samplePoints) {
      try {
        const pois = await searchNearbyPOIs({
          lat: point.lat,
          lng: point.lng,
          radius: maxDistanceFromRoute,
          category,
          limit: 8
        })

        // Deduplicate
        for (const poi of pois) {
          if (!seenIds.has(poi.id)) {
            allPOIs.push(poi)
            seenIds.add(poi.id)
          }
        }
      } catch (error) {
        console.warn('Route POI search failed for sample point:', error)
      }
    }

    return allPOIs
  })
}

export async function searchFeaturedAlongRoute(
  waypoints: { lat: number; lng: number }[],
  maxDistanceFromRoute = 7000
): Promise<POI[]> {
  const cacheKey = buildRouteCacheKey(waypoints, undefined, maxDistanceFromRoute, FEATURED_ROUTE_TAGS)

  return getCachedPOIResults(routePOICache, routePOIRequests, cacheKey, async () => {
    const samplePoints = buildRouteSamplePoints(waypoints, 4)
    const allPOIs: POI[] = []
    const seenIds = new Set<string>()

    for (const point of samplePoints) {
      try {
        const pois = await searchNearbyPOIs({
          lat: point.lat,
          lng: point.lng,
          radius: maxDistanceFromRoute,
          tags: FEATURED_ROUTE_TAGS,
          limit: 6
        })

        for (const poi of pois) {
          if (!seenIds.has(poi.id)) {
            allPOIs.push(poi)
            seenIds.add(poi.id)
          }
        }
      } catch (error) {
        console.warn('Featured route POI search failed for sample point:', error)
      }
    }

    return allPOIs
  })
}

function buildRouteSamplePoints(waypoints: { lat: number; lng: number }[], maxSamples = 8): { lat: number; lng: number }[] {
  if (waypoints.length <= 1) {
    return waypoints
  }

  const segmentLengths = waypoints.slice(1).map((point, index) => {
    return calculateDistance(
      waypoints[index].lat,
      waypoints[index].lng,
      point.lat,
      point.lng
    )
  })

  const totalDistance = segmentLengths.reduce((sum, distance) => sum + distance, 0)
  if (totalDistance === 0) {
    return [waypoints[0]]
  }

  const minimumSamples = Math.max(3, waypoints.length * 2 - 1)
  const distanceBasedSamples = Math.ceil(totalDistance / 25000) + 1
  const sampleCount = Math.min(maxSamples, Math.max(minimumSamples, distanceBasedSamples))

  if (sampleCount <= 1) {
    return [waypoints[0]]
  }

  const points: { lat: number; lng: number }[] = []
  for (let index = 0; index < sampleCount; index++) {
    const targetDistance = (totalDistance * index) / (sampleCount - 1)
    points.push(interpolateRoutePoint(waypoints, segmentLengths, targetDistance))
  }

  return dedupeSamplePoints(points)
}

function interpolateRoutePoint(
  waypoints: { lat: number; lng: number }[],
  segmentLengths: number[],
  targetDistance: number
): { lat: number; lng: number } {
  let accumulatedDistance = 0

  for (let index = 0; index < segmentLengths.length; index++) {
    const segmentLength = segmentLengths[index]
    const start = waypoints[index]
    const end = waypoints[index + 1]

    if (targetDistance <= accumulatedDistance + segmentLength || index === segmentLengths.length - 1) {
      if (segmentLength === 0) {
        return { lat: end.lat, lng: end.lng }
      }

      const ratio = Math.min(1, Math.max(0, (targetDistance - accumulatedDistance) / segmentLength))

      return {
        lat: start.lat + (end.lat - start.lat) * ratio,
        lng: start.lng + (end.lng - start.lng) * ratio
      }
    }

    accumulatedDistance += segmentLength
  }

  const lastWaypoint = waypoints[waypoints.length - 1]
  return { lat: lastWaypoint.lat, lng: lastWaypoint.lng }
}

function dedupeSamplePoints(points: { lat: number; lng: number }[]): { lat: number; lng: number }[] {
  const seen = new Set<string>()

  return points.filter(point => {
    const key = `${point.lat.toFixed(5)}:${point.lng.toFixed(5)}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Build Overpass QL query
 */
function buildOverpassQuery(
  lat: number, 
  lng: number, 
  radius: number, 
  category?: POICategory,
  tags?: string[]
): string {
  const queryTags = tags && tags.length > 0 ? tags : getCategoryTags(category)
  
  // Build node and way queries for each tag
  const queries = queryTags.map(tag => {
    if (tag.includes('=')) {
      return `node["${tag.split('=')[0]}"="${tag.split('=')[1]}"](around:${radius},${lat},${lng});
              way["${tag.split('=')[0]}"="${tag.split('=')[1]}"](around:${radius},${lat},${lng});
              relation["${tag.split('=')[0]}"="${tag.split('=')[1]}"](around:${radius},${lat},${lng});`
    } else {
      return `node["${tag}"](around:${radius},${lat},${lng});
              way["${tag}"](around:${radius},${lat},${lng});
              relation["${tag}"](around:${radius},${lat},${lng});`
    }
  }).join('\n')

  return `
    [out:json][timeout:25];
    (
      ${queries}
    );
    out center;
  `
}

/**
 * Get OSM tags for category
 */
function getCategoryTags(category?: POICategory): string[] {
  if (!category) {
    return [
      'amenity=fuel',
      'amenity=restaurant',
      'amenity=cafe',
      'amenity=fast_food',
      'tourism=hotel',
      'tourism=motel',
      'tourism=camp_site',
      'tourism=attraction',
      'tourism=museum',
      'tourism=viewpoint',
      'tourism=castle',
      'tourism=zoo',
      'tourism=theme_park',
      'tourism=gallery',
      'tourism=aquarium',
      'historic=monument',
      'historic=castle',
      'leisure=park',
      'amenity=parking',
      'highway=rest_area',
      'highway=services',
      'highway=motorway_junction'
    ]
  }

  const categoryMap: Record<POICategory, string[]> = {
    fuel: [
      'amenity=fuel',
      'amenity=charging_station'
    ],
    food: [
      'amenity=restaurant',
      'amenity=cafe',
      'amenity=fast_food',
      'amenity=bar',
      'amenity=pub'
    ],
    accommodation: [
      'tourism=hotel',
      'tourism=motel',
      'tourism=guest_house',
      'tourism=hostel',
      'tourism=camp_site',
      'tourism=caravan_site'
    ],
    attraction: [
      'tourism=attraction',
      'tourism=museum',
      'tourism=viewpoint',
      'tourism=castle',
      'tourism=zoo',
      'tourism=theme_park',
      'tourism=gallery',
      'tourism=aquarium',
      'historic=monument',
      'historic=castle',
      'leisure=park'
    ],
    parking: [
      'amenity=parking',
      'amenity=parking_space'
    ],
    rest_area: [
      'highway=rest_area',
      'highway=services',
      'amenity=toilets'
    ],
    shopping: [
      'shop=supermarket',
      'shop=convenience',
      'shop=mall',
      'amenity=marketplace'
    ]
  }

  return categoryMap[category] || []
}

/**
 * Parse Overpass API response
 */
function parseOverpassResponse(data: any, refLat: number, refLng: number): POI[] {
  const elements = data.elements || []
  const pois: POI[] = []

  for (const element of elements) {
    // Get coordinates (for ways, use center)
    const lat = element.lat || element.center?.lat
    const lng = element.lon || element.center?.lon

    if (!lat || !lng) continue

    // Get name
    const name = element.tags?.name || 
                 element.tags?.brand || 
                 element.tags?.operator || 
                 'Unnamed'

    // Determine type and category
    const { type, category } = determineTypeAndCategory(element.tags)

    const poi: POI = {
      id: `osm-${element.type}-${element.id}`,
      name,
      type,
      category,
      lat,
      lng,
      distance: calculateDistance(refLat, refLng, lat, lng),
      tags: element.tags || {},
      address: buildAddress(element.tags),
      phone: element.tags?.phone,
      website: element.tags?.website,
      openingHours: element.tags?.opening_hours
    }

    pois.push(poi)
  }

  return pois
}

/**
 * Determine POI type and category from OSM tags
 */
function determineTypeAndCategory(tags: Record<string, string>): { type: string; category: string } {
  if (tags.place === 'city') return { type: 'city', category: 'settlement' }
  if (tags.place === 'town') return { type: 'town', category: 'settlement' }
  if (tags.place === 'village') return { type: 'village', category: 'settlement' }
  if (tags.place === 'hamlet') return { type: 'hamlet', category: 'settlement' }
  if (tags.place === 'suburb') return { type: 'suburb', category: 'settlement' }
  if (tags.place === 'municipality') return { type: 'municipality', category: 'settlement' }
  if (tags.amenity === 'fuel') return { type: 'gas_station', category: 'fuel' }
  if (tags.amenity === 'charging_station') return { type: 'ev_charging', category: 'fuel' }
  if (tags.amenity === 'restaurant') return { type: 'restaurant', category: 'food' }
  if (tags.amenity === 'cafe') return { type: 'cafe', category: 'food' }
  if (tags.amenity === 'fast_food') return { type: 'fast_food', category: 'food' }
  if (tags.tourism === 'hotel') return { type: 'hotel', category: 'accommodation' }
  if (tags.tourism === 'motel') return { type: 'motel', category: 'accommodation' }
  if (tags.tourism === 'camp_site') return { type: 'camping', category: 'accommodation' }
  if (tags.tourism === 'attraction') return { type: 'attraction', category: 'attraction' }
  if (tags.tourism === 'museum') return { type: 'museum', category: 'attraction' }
  if (tags.tourism === 'viewpoint') return { type: 'viewpoint', category: 'attraction' }
  if (tags.tourism === 'castle') return { type: 'attraction', category: 'attraction' }
  if (tags.tourism === 'zoo') return { type: 'zoo', category: 'attraction' }
  if (tags.tourism === 'theme_park') return { type: 'theme_park', category: 'attraction' }
  if (tags.tourism === 'gallery') return { type: 'gallery', category: 'attraction' }
  if (tags.tourism === 'aquarium') return { type: 'aquarium', category: 'attraction' }
  if (tags.historic === 'monument') return { type: 'attraction', category: 'attraction' }
  if (tags.historic === 'castle') return { type: 'attraction', category: 'attraction' }
  if (tags.leisure === 'park') return { type: 'park', category: 'attraction' }
  if (tags.amenity === 'parking') return { type: 'parking', category: 'parking' }
  if (tags.highway === 'rest_area') return { type: 'rest_area', category: 'rest_area' }
  if (tags.highway === 'services') return { type: 'rest_area', category: 'rest_area' }
  if (tags.highway === 'motorway_junction') return { type: 'other', category: 'other' }
  if (tags.shop) return { type: 'shopping', category: 'shopping' }
  
  return { type: 'other', category: 'other' }
}

/**
 * Build address string from OSM tags
 */
function buildAddress(tags: Record<string, string>): string | undefined {
  const parts = []
  
  if (tags['addr:street']) {
    parts.push(tags['addr:street'])
    if (tags['addr:housenumber']) {
      parts[parts.length - 1] += ' ' + tags['addr:housenumber']
    }
  }
  
  if (tags['addr:city']) parts.push(tags['addr:city'])
  if (tags['addr:postcode']) parts.push(tags['addr:postcode'])
  
  return parts.length > 0 ? parts.join(', ') : undefined
}

/**
 * Calculate distance between two coordinates in meters (Haversine formula)
 */
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3 // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lng2 - lng1) * Math.PI / 180

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return Math.round(R * c) // Distance in meters
}

/**
 * Format distance for display
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${meters} m`
  }
  return `${(meters / 1000).toFixed(1)} km`
}
